// Ein PDF aus JPEG-Seiten — ohne Bibliothek (29.10.2026).
//
// Für den Druck aus der iPhone-App über das Teilen-Menü braucht es eine
// Datei; ein PDF druckt iOS im A4-Format, ein Bild würde es frei auf die
// Seite skalieren. Mehr als „ein Bild pro Seite" brauchen wir nicht, und
// dafür reicht das PDF-Grundgerüst: Katalog, Seitenbaum, je Seite ein
// Bildobjekt (DCTDecode — das JPEG geht unverändert hinein) und ein
// Inhaltsstrom, der es oben bündig über die volle Breite zeichnet.
//
// Eine Bibliothek (jsPDF) brächte dafür rund 300 kB mit. Diese Datei ist
// nachprüfbar klein, und pdfAusBildern.test.ts liest das Ergebnis mit.

export type PdfSeite = {
  /** Rohbytes eines JPEG. */
  jpeg: Uint8Array;
  /** Pixelmaße des JPEG. */
  breite: number;
  hoehe: number;
};

/** A4 in Punkt (1/72 Zoll). */
export const A4_BREITE_PT = 595.28;
export const A4_HOEHE_PT = 841.89;

const kodierer = new TextEncoder();

export function pdfAusBildern(seiten: PdfSeite[]): Blob {
  if (seiten.length === 0) throw new Error('keine Seiten');
  const teile: Uint8Array[] = [];
  const versaetze: number[] = [];
  let laenge = 0;

  const schreibe = (t: string | Uint8Array): void => {
    const b = typeof t === 'string' ? kodierer.encode(t) : t;
    teile.push(b);
    laenge += b.length;
  };
  const objekt = (nr: number, inhalt: () => void): void => {
    versaetze[nr] = laenge;
    schreibe(`${nr} 0 obj\n`);
    inhalt();
    schreibe('\nendobj\n');
  };

  // Objektnummern: 1 Katalog, 2 Seitenbaum, dann je Seite 3 Objekte
  // (Seite, Bild, Inhalt).
  const seitenNr = seiten.map((_, i) => 3 + i * 3);

  schreibe('%PDF-1.4\n%âãÏÓ\n');
  objekt(1, () => schreibe('<< /Type /Catalog /Pages 2 0 R >>'));
  objekt(2, () =>
    schreibe(`<< /Type /Pages /Kids [${seitenNr.map((n) => `${n} 0 R`).join(' ')}] /Count ${seiten.length} >>`)
  );

  seiten.forEach((s, i) => {
    const seite = seitenNr[i];
    const bild = seite + 1;
    const inhalt = seite + 2;
    // Über die volle Breite, oben bündig; die Höhe folgt dem Bild.
    const h = (A4_BREITE_PT * s.hoehe) / s.breite;
    const y = A4_HOEHE_PT - h;
    const strom = `q ${A4_BREITE_PT.toFixed(2)} 0 0 ${h.toFixed(2)} 0 ${y.toFixed(2)} cm /Im0 Do Q`;

    objekt(seite, () =>
      schreibe(
        `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${A4_BREITE_PT} ${A4_HOEHE_PT}] ` +
          `/Resources << /XObject << /Im0 ${bild} 0 R >> >> /Contents ${inhalt} 0 R >>`
      )
    );
    objekt(bild, () => {
      schreibe(
        `<< /Type /XObject /Subtype /Image /Width ${s.breite} /Height ${s.hoehe} ` +
          `/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${s.jpeg.length} >>\nstream\n`
      );
      schreibe(s.jpeg);
      schreibe('\nendstream');
    });
    objekt(inhalt, () => schreibe(`<< /Length ${strom.length} >>\nstream\n${strom}\nendstream`));
  });

  const anzahl = 3 + seiten.length * 3;
  const xref = laenge;
  schreibe(`xref\n0 ${anzahl}\n0000000000 65535 f \n`);
  for (let n = 1; n < anzahl; n++) {
    schreibe(`${String(versaetze[n]).padStart(10, '0')} 00000 n \n`);
  }
  schreibe(`trailer\n<< /Size ${anzahl} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`);

  return new Blob(teile as BlobPart[], { type: 'application/pdf' });
}
